/**
 * Labstep
 */

import { ChemicalMimeType, SupportedFormat } from 'ketcher-core';
import type { Indigo } from 'ketcher-core/dist/application/indigo';
import bugsnagService from 'labstep-web/services/bugsnag.service';
import ketcherService from './ketcher';

const REACTION_ARROW = '>>';

export interface SmilesSplit {
  reactants: string[];
  products: string[];
}

export const splitSmiles = (smiles: string): SmilesSplit => {
  const reaction = smiles.split(REACTION_ARROW);
  const reactants = reaction.length > 0 ? reaction[0].split('.') : [];
  const products = reaction.length > 1 ? reaction[1].split('.') : [];

  return {
    reactants,
    products,
  };
};

/**
 *
 * @description Remove all characters after a substring including the substring
 * @param str Full string
 * @param substring Substring
 * @returns Updated string
 */
export const removeAfterSubstring = (
  str: string,
  substring: string,
): string => {
  const index = str.indexOf(substring);
  if (index > -1) {
    return str.substring(0, index);
  }
  return str;
};

/**
 *
 * @description Sanitize InChI string to remove auxillary information
 * @param inchi string
 * @returns string
 */
export const sanitizeInchi = (inchi: string): string => {
  let sanitizedInchi = removeAfterSubstring(inchi, 'AuxInfo').split(
    ' ',
  )[0];
  if (sanitizedInchi.endsWith('\n')) {
    sanitizedInchi = sanitizedInchi.slice(0, -1);
  }
  return sanitizedInchi;
};

export const getInChI = async (
  smiles: string,
  indigo?: Indigo,
): Promise<(string | void)[]> => {
  const smilesSplit = splitSmiles(smiles);
  const allSmiles = [
    ...smilesSplit.reactants,
    ...smilesSplit.products,
  ];

  const promises: Promise<string | void>[] = [];
  allSmiles.forEach((smile) => {
    if (indigo) {
      promises.push(
        indigo
          .convert(smile, {
            outputFormat: ChemicalMimeType.InChI,
            inputFormat: ChemicalMimeType.DaylightSmiles,
          })
          .then((output) =>
            output.struct ? sanitizeInchi(output.struct) : '',
          )
          .catch((e) => bugsnagService.notify(e, null, 'info')),
      );
    } else {
      promises.push(
        (async () => {
          try {
            if (ketcherService.isReady()) {
              const output = await ketcherService.getStructure(
                smile,
                'inchi',
                'smiles',
              );
              return output ? sanitizeInchi(output) : '';
            } else {
              // Fallback: if Ketcher service is not ready, return empty string
              console.warn(
                'Ketcher service not ready, skipping InChI generation for:',
                smile,
              );
              return '';
            }
          } catch (e: any) {
            bugsnagService.notify(e, null, 'info');
            return '';
          }
        })(),
      );
    }
  });

  return await Promise.all(promises).then((values) =>
    values.filter((v) => v),
  );
};

export function identifyStructFormat(
  stringifiedStruct: string,
  isMacromolecules = false,
): SupportedFormat {
  // Mimic Indigo/molecule_auto_loader.cpp as much as possible
  const sanitizedString = stringifiedStruct.trim();

  try {
    if (JSON.parse(sanitizedString)) {
      return SupportedFormat.ket;
    }
  } catch (e) {}

  const isRXN = sanitizedString.includes('$RXN');
  const isSDF = sanitizedString.includes('\n$$$$');
  const isV2000 = sanitizedString.includes('V2000');
  const isV3000 = sanitizedString.includes('V3000');

  if (isRXN) {
    return SupportedFormat.rxn;
  }

  if (isSDF) {
    if (isV2000) {
      return SupportedFormat.sdf;
    }
    return SupportedFormat.sdfV3000;
  }

  if (isV2000) {
    return SupportedFormat.mol;
  }

  if (isV3000) {
    return SupportedFormat.molV3000;
  }

  const match = sanitizedString.match(/^(M {2}END|\$END MOL)$/m);

  if (match) {
    const end = (match.index || 0) + match[0].length;
    if (
      end === sanitizedString.length ||
      sanitizedString
        .slice(end, end + 20)
        .search(/^\$(MOL|END CTAB)$/m) !== -1
    ) {
      return SupportedFormat.mol;
    }
  }

  if (
    sanitizedString[0] === '<' &&
    sanitizedString.indexOf('<molecule') !== -1
  ) {
    return SupportedFormat.cml;
  }

  const clearStr = sanitizedString
    .replace(/\s/g, '')
    .replace(/(\\r)|(\\n)/g, '');
  const isBase64String =
    /^([0-9a-zA-Z+/]{4})*(([0-9a-zA-Z+/]{2}==)|([0-9a-zA-Z+/]{3}=))?$/;
  const cdxHeader = 'VjCD0100';
  if (
    clearStr.length % 4 === 0 &&
    isBase64String.test(clearStr) &&
    window.atob(clearStr).startsWith(cdxHeader)
  ) {
    return SupportedFormat.cdx;
  }

  if (sanitizedString.slice(0, 5) === 'InChI') {
    return SupportedFormat.inChI;
  }

  if (sanitizedString.indexOf('\n') === -1 && !isMacromolecules) {
    // TODO: smiles regexp
    return SupportedFormat.smiles;
  }

  if (sanitizedString.indexOf('<CDXML') !== -1) {
    return SupportedFormat.cdxml;
  }

  if (sanitizedString[0] === '>') {
    return SupportedFormat.fasta;
  }

  if (/^[a-zA-Z\s\n]*$/.test(sanitizedString)) {
    return SupportedFormat.sequence;
  }

  return SupportedFormat.unknown;
}

/**
 * Get SMILES string from molecule data
 *
 * Only supports mol, smiles, and ket format
 *
 * @param data molecule data field
 * @returns SMILES
 */
export const getSmiles = async (
  data: string,
  indigo?: Indigo,
): Promise<string> => {
  if (indigo) {
    const output = await indigo.convert(data, {
      outputFormat: ChemicalMimeType.DaylightSmiles,
    });
    return output.struct;
  }

  // Use Ketcher service as fallback
  if (ketcherService.isReady()) {
    return await ketcherService.getStructure(data, 'smiles', null);
  }

  // If Ketcher service is not ready, throw an error
  throw new Error(
    'No conversion service available. Ketcher service not initialized.',
  );
};
